import axios, { CancelTokenSource } from 'axios';
import * as Routes from '@/modules/routes';
import { AssessmentFilter, AssessmentFilterField, AssessmentFilterParams, AssessmentInfo } from './types';

export const initialFilters: AssessmentFilter = {
  category: [],
  page: 1,
  per_page: 15,
  source: [],
};

const DEFAULT_SOURCE_FILTER_VALUES = ['tci', 'my_tests'];

export const getFilters = (programId: number | string, lessonId?: number | string) => {
  const filters = { ...initialFilters };

  return (
    axios
      .get<AssessmentFilter>(Routes.plato_api_filter_categories_assessments_path({ program_id: programId }))
      .then((res) => {
        const filterData = res.data;

        filters.unit = filterData.unit?.map((unit) => {
          const label = Object.keys(unit)[0];
          const unitData = Object.values(unit)[0];
          const lessons = unitData.lessons.map((lessonFilter: AssessmentFilterField) => ({
            ...lessonFilter, checked: lessonFilter.value === lessonId,
          }));

          return { label, lessons, value: unitData.value };
        });

        filters.lesson = filterData.lesson?.map(lessonFilter => ({
          ...lessonFilter, checked: lessonFilter.value === lessonId,
        }));

        filters.category = res.data.category.map(categoryFilter => ({
          ...categoryFilter, checked: false,
        })).sort((categoryA, categoryB) => (
          categoryA.label.toLocaleLowerCase() > categoryB.label.toLocaleLowerCase() ? 1 : -1
        ));

        filters.source = res.data.source.map(sourceFilter => ({
          ...sourceFilter,
          checked: DEFAULT_SOURCE_FILTER_VALUES.includes(sourceFilter.value as string),
        }));

        return filters;
      })
  );
};

let filterCancelToken: CancelTokenSource;
export const getFilteredAssessments = (
  filters: AssessmentFilter,
  programId: string | number,
) => {
  if (filterCancelToken !== undefined) {
    filterCancelToken.cancel('Operation canceled due to new request.');
  }

  filterCancelToken = axios.CancelToken.source();

  const params = {
    column: 'name',
    order: 'asc',
    page: filters.page,
    per_page: filters.per_page,
    'search[category_in]': [],
    'search[lesson_ids_in]': [],
    'search[program_id_eq]': programId,
    'search[source_in]': [],
  } as AssessmentFilterParams;

  filters.unit?.forEach((unitFilers) => {
    const checkedLessons = unitFilers.lessons
      .filter(lessonFilter => lessonFilter.checked)
      .map(lessonFilter => lessonFilter.value);

    params['search[lesson_ids_in]'].push(...checkedLessons);
  });

  filters.lesson?.forEach((lessonFilter) => {
    if (lessonFilter.checked) {
      params['search[lesson_ids_in]'].push(lessonFilter.value);
    }
  });

  filters.category
    .filter(categoryFilter => categoryFilter.checked)
    .forEach((categoryFilter) => {
      params['search[category_in]'].push(categoryFilter.value);
    });

  filters.source.forEach((sourceFilter) => {
    if (sourceFilter.checked) {
      params['search[source_in]'].push(sourceFilter.value);
    }
  });

  return axios.request<{ data: AssessmentInfo[]; meta: { count: number } }>({
    cancelToken: filterCancelToken.token,
    method: 'get',
    params,
    url: Routes.plato_api_assessments_path(),
  }).then(res => ({
    count: res.data.meta.count,
    data: res.data.data,
  }));
};
